/**
 * #466 · El cálculo de la herramienta RITE — Climatización, con el Excel de frío de Dirección.
 *
 * Es el `rite-engine.js` de la maqueta aprobada el 09/10/2026 (material fijado en el tag
 * `referencia-rite-2026-10-48b0af7` del orquestador, carpeta `maquetas/rite-2026-10/`), traído tal
 * cual: **la misma firma y los mismos nombres**. No es por comodidad: las 255 pruebas de
 * `rite-tests.js` se ejecutan contra este fichero sin tocarles una línea
 * (`node scripts/prueba-rite-motor.js`), y eso sólo vale como acreditación si lo que prueban es
 * esto y no una traducción parecida.
 *
 * Vive fuera de `tarifa-calculador-v3.vue` por dos razones. Una, el cálculo de bandas no cabe en
 * `formula.tpc` —la cadena de JavaScript que la herramienta guarda en Mongo y que la pantalla
 * evalúa con `new Function`—: son seis rangos, un «primer generador» que se cobra una vez, dos
 * factores de 2ª distintos y el reparto frío/calor de la mixta. Y dos, se puede ejercer en Node sin
 * montar la pantalla, que es lo que permite comparar con el Excel.
 *
 * Los NÚMEROS no están aquí por gusto: los trae la definición de la herramienta desde
 * `tarifa_herramientas` (`config.rite`), que es lo que está versionado y lo que Dirección puede
 * cambiar sin tocar código. Lo de abajo son los valores de la versión aprobada, y hacen de relleno
 * para que la pantalla calcule igual si la configuración llegara incompleta: `parametros()` fusiona
 * lo que venga encima. Mismo criterio que el `CFG` de relleno del calculador.
 */

/** Hoja «PARAMETROS Y TIEMPOS» del Excel «Calculadora ofertas RITE aire acondicionado». */
const BANDS = [
  { k: 'b1', l: '12–35 kW', c: 50, d: 25, e: 0.5 },
  { k: 'b2', l: '36–70 kW', c: 60, d: 30, e: 0.5 },
  { k: 'b3', l: '71–150 kW', c: 75, d: 40, e: 0.533333333 },
  { k: 'b4', l: '151–400 kW', c: 90, d: 45, e: 0.5 },
  { k: 'b5', l: '401–700 kW', c: 105, d: 60, e: 0.571428571 },
  { k: 'b6', l: '> 700 kW', c: 120, d: 75, e: 0.625 }
];

/**
 * `doc` documental y preparación (una vez por instalación) y `fdoc` su factor de 2ª; `uta1`/`utaN`
 * los minutos de la primera UTA y de cada una de las siguientes, con `futa` de factor; `zona` los
 * minutos por edificio o zona separada a partir de la segunda; `red` el escalón del redondeo del
 * importe de trabajo; `failMin` el mínimo de la visita fallida y `failImp` sus minutos improductivos.
 */
const P = { doc: 30, fdoc: 0.5, uta1: 18, utaN: 12, futa: 0.5, zona: 15, red: 5, failMin: 175, failImp: 60 };

/** Calor: la fórmula que ya tenía la herramienta (v8, 07/10/2026). No la cambia esta tarea. */
const CALOR = { p: { per: { base: 60, kw: 0.3 }, ini: { base: 90, kw: 0.4 } }, porGen: 10, tpiPct: 0.12, f2: 0.4 };

const DEFECTO = { BANDS: BANDS, P: P, CALOR: CALOR };

/**
 * Los parámetros con los que se calcula: los de la herramienta encima de los de arriba.
 *
 * La fusión es por bloque y no profunda a propósito. Una tabla de bandas a medias —tres rangos de
 * la configuración y tres del relleno— sería un cálculo que nadie ha aprobado; o manda la tabla de
 * la herramienta entera, o manda la de la versión aprobada.
 *
 * Y un bloque sólo manda si está COMPLETO. La definición la lee la pantalla de `tarifa_herramientas`
 * y puede llegar a medias —una versión editada a mano, una reabierta de una línea guardada—: con un
 * `calor.p` sin `per`, `par.CALOR.p[S.mod] || par.CALOR.p.per` daba `undefined` y el cálculo moría
 * en `q.base`, o sea la pantalla sin precio y sin decir por qué. Lo mismo con una banda a nulo
 * dentro de la tabla. Este es el único sitio por el que pasa la definición antes de ser parámetros,
 * así que la comprobación vive aquí y no repartida por cada lectura.
 */
function bandaCompleta(b) {
  return !!b && typeof b === 'object' && typeof b.k === 'string'
    && typeof b.c === 'number' && typeof b.d === 'number' && typeof b.e === 'number';
}

function formulaDeCalorCompleta(calor) {
  if (!calor || typeof calor !== 'object' || !calor.p || typeof calor.p !== 'object') return false;
  // Los tres de fuera cuentan igual que las dos fórmulas: sin `porGen` el precio sale NaN, y un
  // presupuesto que enseña «NaN €» es peor que uno que no sale.
  if (['porGen', 'tpiPct', 'f2'].some(function (k) { return typeof calor[k] !== 'number'; })) return false;
  return ['per', 'ini'].every(function (modo) {
    const q = calor.p[modo];
    return !!q && typeof q.base === 'number' && typeof q.kw === 'number';
  });
}

function parametros(configRite) {
  const c = configRite || {};
  const bandas = Array.isArray(c.bandas) && c.bandas.length && c.bandas.every(bandaCompleta) ? c.bandas : BANDS;
  return {
    BANDS: bandas,
    P: Object.assign({}, P, c.p || {}),
    CALOR: formulaDeCalorCompleta(c.calor) ? c.calor : CALOR
  };
}

/**
 * Un número para una etiqueta, con la coma decimal española.
 *
 * Los números que se nombran en el desglose salen SIEMPRE de `par`, nunca escritos a mano en el
 * texto: el desglose viaja al presupuesto impreso, y una etiqueta fija explica una operación
 * distinta de la que produjo el importe en cuanto Dirección cambia ese valor en la configuración de
 * la herramienta. Es el hallazgo de la ronda 1 de la revisión, y por eso la conversión está aquí y
 * no repetida en cada `push`.
 */
function num(v) { return String(v).replace('.', ','); }

/** Superficie servida: hasta 500 m² no suma, y de ahí en cuatro escalones hasta 60 min. */
function supMin(m) { return m <= 500 ? 0 : m <= 2000 ? 15 : m <= 5000 ? 30 : m <= 10000 ? 45 : 60; }

/**
 * Redondeo a 5 € hacia arriba del importe de trabajo (decisión de Dirección, 09/10/2026).
 *
 * El `-1e-9` es el que evita que un importe que ya es múltiplo de 5 suba un escalón entero por el
 * error de coma flotante: `645 / 5` puede salir `129.00000000000001` y `Math.ceil` lo subiría a
 * 650. El ejemplo del Excel da exactamente 645 €, así que el defecto saldría en el primer caso.
 */
function ceil5(v, red) {
  const paso = red || P.red;
  return Math.ceil(v / paso - 1e-9) * paso;
}

/** Minutos de campo de calor: base + kW × factor + 10 × generadores. */
function calorTpc(S, PAR) {
  const par = PAR || DEFECTO;
  const q = par.CALOR.p[S.mod] || par.CALOR.p.per;
  return q.base + (S.kw || 0) * q.kw + (S.ngc || 1) * par.CALOR.porGen;
}

/**
 * Minutos de frío de una visita (`sec=false` la 1ª, `sec=true` la 2ª). Es F15:F24 y B29 del Excel.
 *
 * Todos los equipos cuentan como **adicionales** y se suma **una sola vez** la mayor diferencia
 * «1.er generador − adicional» de los rangos que tienen equipos: así no se cobra un primer
 * generador por cada rango. En la 2ª, cada término va por el factor de su propio rango, que es por
 * lo que la diferencia se pesa dentro del `Math.max` y no después.
 */
function frio(S, sec, PAR) {
  const par = PAR || DEFECTO;
  let n = 0, gen = 0, inc = 0;
  const lines = [];
  par.BANDS.forEach(function (b) {
    const q = S[b.k] || 0;
    if (!q) return;
    n += q;
    const f = sec ? b.e : 1, t = q * b.d * f;
    gen += t;
    inc = Math.max(inc, (b.c - b.d) * f);
    lines.push([q + ' × ' + b.l, t]);
  });
  if (!n) inc = 0;
  const uta = S.uta ? (par.P.uta1 + Math.max(S.uta - 1, 0) * par.P.utaN) * (sec ? par.P.futa : 1) : 0;
  return { n: n, gen: gen, inc: inc, uta: uta, lines: lines };
}

/**
 * El cálculo completo de una de las seis inspecciones.
 *
 * `S` son los datos tecleados: `fam` (`frio` | `calor` | `mixta`), `mod` (`per` | `ini`), `b1..b6`
 * los equipos de frío por rango, `uta`, `emi`, `sup`, `zon`, `kw`, `ngc`, el desplazamiento
 * (`km`, `mismo`), los descuentos en tanto por ciento (`dto`, `dto2`), los recargos (`fs`, `noc`) y
 * los gastos de cada visita (`g`, `g2`). `CFG` es la configuración global: `ph`, `kmmin`, `tdf`,
 * `kmcost`, las dietas y los porcentajes de recargo.
 */
function calc(S, CFG, PAR) {
  const par = PAR || DEFECTO;
  const fam = S.fam, hasFrio = fam !== 'calor', hasCalor = fam !== 'frio';
  const r = { why1: [], tpc: 0, tpc2: 0, tpi: 0, tpi2: 0, nFrio: 0 };
  const sup = supMin(S.sup || 0), zon = Math.max((S.zon || 1) - 1, 0) * par.P.zona;
  if (hasFrio) {
    const a = frio(S, false, par), b = frio(S, true, par);
    r.tpc += a.gen + a.inc + a.uta; r.tpc2 += b.gen + b.inc + b.uta; r.nFrio = a.n;
    r.frio1 = a; r.frio2 = b;
    a.lines.forEach(function (l) { r.why1.push(['Frío · ' + l[0], l[1]]); });
    if (a.inc) r.why1.push(['Frío · 1.er generador, una vez', a.inc]);
    if (a.uta) r.why1.push(['Frío · ' + S.uta + ' UTA' + (S.uta > 1 ? 's' : ''), a.uta]);
    // Superficie y zonas son de la instalación: una vez, y sin factor de 2ª (B30 y B31 del Excel)
    r.tpc += sup + zon; r.tpc2 += sup + zon;
    if (sup) r.why1.push(['Superficie ' + (S.sup || 0).toLocaleString('es-ES') + ' m²', sup]);
    if (zon) r.why1.push(['Zonas adicionales · ' + (S.zon - 1), zon]);
    r.tpi = par.P.doc; r.tpi2 = par.P.doc * par.P.fdoc;
  }
  if (hasCalor) {
    const c1 = calorTpc(S, par), q = par.CALOR.p[S.mod] || par.CALOR.p.per;
    r.tpc += c1; r.tpc2 += c1 * par.CALOR.f2; r.calor = c1;
    r.why1.push(['Calor · base ' + (S.mod === 'per' ? 'periódica' : 'inicial'), q.base]);
    if (S.kw) r.why1.push(['Calor · ' + S.kw + ' kW × ' + num(q.kw), S.kw * q.kw]);
    // El factor sale de `par`, no escrito a mano: este texto va al desglose de minutos de la ficha
    // impresa, y con un 10 fijo el presupuesto explicaría una operación distinta de la que produjo
    // el importe en cuanto Dirección moviera los minutos por generador en la configuración.
    r.why1.push(['Calor · ' + (S.ngc || 1) + ' generador' + ((S.ngc || 1) === 1 ? '' : 'es')
      + ' × ' + num(par.CALOR.porGen), (S.ngc || 1) * par.CALOR.porGen]);
    // Sólo calor: el documental es el 12% del campo. En mixta son los 30 min de la instalación, que
    // ya los ha puesto el bloque de frío: contar los dos sería cobrar dos veces la misma preparación.
    if (!hasFrio) { r.tpi = c1 * par.CALOR.tpiPct; r.tpi2 = r.tpi * par.CALOR.f2; }
  }
  r.why1.push(['Documental y preparación (TPI)', r.tpi]);
  const km = S.mismo ? 0 : Math.max(S.km || 0, CFG.kmmin), td = S.mismo ? 0 : km * CFG.tdf, kmc = km * CFG.kmcost;
  function price(tpc, tpi, dto, g) {
    const U = tpc + tpi + td, trab = ceil5(U / 60 * CFG.ph, par.P.red) * (1 - (dto || 0) / 100);
    const base = trab + kmc, rfs = S.fs ? base * CFG.pctFS / 100 : 0, rn = S.noc ? base * CFG.pctNoc / 100 : 0;
    const gg = g || {};
    const gs = [['Subcontratación', gg.sub || 0], ['Peajes y parking', gg.peaj || 0], ['Comidas', (gg.ncom || 0) * CFG.com], ['Cenas', (gg.ncen || 0) * CFG.cen], ['Hotel', (gg.nhot || 0) * CFG.hot], ['Desayunos', (gg.ndes || 0) * CFG.des]].filter(function (x) { return x[1]; });
    const gt = gs.reduce(function (s, x) { return s + x[1]; }, 0);
    return { U: U, trab: trab, kmc: kmc, rfs: rfs, rn: rn, gs: gs, gastos: gt, total: base + rfs + rn + gt };
  }
  r.km = km; r.td = td; r.U = r.tpc + r.tpi + td;
  r.p1 = price(r.tpc, r.tpi, S.dto, S.g); r.p2 = price(r.tpc2, r.tpi2, S.dto2, S.g2);
  // Visita fallida (decisión de Dirección, 09/10/2026): se cobra el desplazamiento como una partida más,
  // tiempo (ida y vuelta + 60 min improductivos) al precio/hora y kilómetros a su coste, con el mínimo del Excel
  const ft = ceil5((td + par.P.failImp) / 60 * CFG.ph, par.P.red);
  // `imp` y `desp` van en el desglose porque la pantalla y el PDF tienen que poder DECIR de qué se
  // componen esos minutos sin escribir el número a mano: con un «60 improductivos» fijo, mover
  // `failImp` en la configuración dejaba el desglose contradiciendo a su propio total.
  r.failD = { min: td + par.P.failImp, desp: td, imp: par.P.failImp, tiempo: ft, km: kmc, minimo: par.P.failMin };
  r.fail = Math.max(ft + kmc, par.P.failMin);
  const miss = [];
  if (hasFrio && !r.nFrio) miss.push('Falta indicar los generadores de frío');
  if (hasCalor && !(S.ngc > 0)) miss.push('Falta el número de generadores de calor');
  if (hasFrio && !(S.sup > 0)) miss.push('Falta la superficie');
  if (!(S.zon > 0)) miss.push('Falta el número de zonas o edificios');
  r.miss = miss;
  return r;
}

/**
 * El cálculo de frío **tal cual lo hace la hoja**: el desplazamiento se teclea en minutos, no hay
 * kilómetros y el precio/hora es el del Excel. No lo usa la pantalla —ahí manda la configuración
 * global, que es lo que decidió Dirección— y existe sólo para poder comparar celda a celda con el
 * Excel recalculado en LibreOffice (F24, B29, B30, B31, B32, B33, B37 y E28).
 */
function excelFrio(S, segunda, despMin, ph, PAR) {
  const par = PAR || DEFECTO;
  const a = frio(S, segunda, par);
  const sup = supMin(S.sup || 0), zon = Math.max((S.zon || 1) - 1, 0) * par.P.zona;
  const doc = par.P.doc * (segunda ? par.P.fdoc : 1);
  const campo = a.gen + a.inc + a.uta + sup + zon + doc;
  return {
    gen: a.gen + a.inc, uta: a.uta, sup: sup, zon: zon, doc: doc, campo: campo,
    precio: ceil5((campo + despMin) / 60 * ph, par.P.red),
    fallida: Math.max(ceil5((despMin + par.P.failImp) / 60 * ph, par.P.red), par.P.failMin)
  };
}

const RiteMotor = {
  BANDS: BANDS, P: P, CALOR: CALOR, DEFECTO: DEFECTO,
  parametros: parametros, calc: calc, frio: frio, supMin: supMin, ceil5: ceil5,
  excelFrio: excelFrio, calorTpc: calorTpc
};

export default RiteMotor;
export { BANDS, P, CALOR, DEFECTO, parametros, calc, frio, supMin, ceil5, excelFrio, calorTpc };
